/**
 * Browser-side helper for the two-step direct-to-storage upload flow.
 *
 * Files are uploaded directly from the browser to object storage via a
 * short-lived signed URL — bypassing Vercel's 4.5 MB serverless body limit so
 * 50 MB lot bidder packets land successfully. The helper orchestrates the
 * three round trips so per-entity callers (customer documents, lot/auction
 * documents, inventory media, the hero and logo images) stay tiny and
 * consistent.
 *
 *   1. POST /api/files/upload-url   — auth + tenant + ownership + MIME check;
 *                                    returns a signed URL the browser PUTs to
 *   2. PUT {signedUrl}              — file body goes straight to object storage;
 *                                    Vercel never sees it
 *   3. POST /api/files/upload-complete — re-validates + writes metadata (RPC
 *                                    or DB insert) and returns the attachment
 *
 * Each per-entity caller wraps the returned attachment into its own domain
 * shape (`doc`, `mediaItem`, etc.) so existing call sites keep their public
 * type signatures.
 */

import { parseResponse } from '@auctogon/core/api';
import type { ApiEnvelope } from '@auctogon/core/api/registry';
import type {
  UploadCompleteDto,
  UploadedAttachmentDto,
  UploadUrlDto,
} from '@auctogon/core-admin/schemas/responses/files';
import { api } from './typed-client';
import type { EntityType } from '@auctogon/admin-isomorphic/isomorphic/file-entities';

/** The file-entity taxonomy, from the client-safe `@/lib/file-entities` module
 *  (BUG-1231 — was a hand-maintained duplicate of the server-side EntityType).
 *  BL-963 added `auction_hero` + `tenant_logo` to `EntityType` there. */
export type UploadEntityType = EntityType;

/** Attachment shape returned by /api/files/upload-complete — the shared DTO (BL-2016). */
export type UploadAttachment = UploadedAttachmentDto;

/** Discriminated-union result from the helper. Success carries the attachment;
 *  failure carries a user-safe error string the caller surfaces directly. */
export type UploadViaSignedUrlResult =
  | { success: true; attachment: UploadAttachment; warning?: string }
  /** `fieldErrors`: the complete step's per-field refusals, keyed like the caller's form (BL-2523). */
  | { success: false; error: string; fieldErrors?: Record<string, string> };

/** Inputs to {@link uploadViaSignedUrl}. */
export interface UploadViaSignedUrlParams {
  entityType: UploadEntityType;
  entityId: string;
  tenantId: string;
  file: File | Blob;
  /** Override the file's `.name` (Blob has no name). Defaults to `file.name` for File. */
  fileName?: string;
  /** Override the file's `.type`. Defaults to `file.type` then `application/octet-stream`. */
  mimeType?: string;
  /** Customer documents pass `{ documentType, documentName, isAccountingOnly }` here. */
  extraMetadata?: Record<string, unknown>;
  /** BL-2816: the storage path of the item photo this upload is the thumbnail of; the server mints the slot beside it. */
  thumbnailOf?: string;
  /** BL-2848: the storage path of the item video this upload is the poster still of; the server mints the slot at the registry's `.poster.jpg` beside it. */
  posterOf?: string;
  /** BL-2838: the file is for a record the Add form has not created yet; `entityId` is the id the browser minted. */
  newRecord?: true;
}

/** Optional progress hook — fires once per phase. Useful for fine-grained
 *  status text like "Uploading…" vs "Saving…". Most callers can ignore this. */
export type UploadPhase = 'presign' | 'put' | 'complete';

/** Max attempts per sub-request: initial + 1 retry on a transient failure. */
const MAX_ATTEMPTS = 2;

/** What storage answers a create-only PUT whose object already exists (BL-2550). */
const PRECONDITION_FAILED = 412;

/**
 * Delay between retry attempts, in milliseconds. Defaults to `0` — the common
 * cause of a transient 5xx is per-request load on object storage rather than
 * a server that needs cool-down time, so an immediate retry is usually enough.
 * Kept as a named constant so a future incident can dial in a backoff without
 * touching the retry logic itself.
 */
const RETRY_DELAY_MS = 0;

/**
 * Run a fetch-returning callback with one retry on a transient failure.
 *
 * A transient failure is either a thrown error (network blip, DNS, abort) or
 * a response in the 5xx range. 4xx responses flow through unchanged because
 * they are user-actionable (file too large, validation error) — retrying won't
 * change the outcome. A 2xx response is returned immediately.
 *
 * If both attempts throw, the second error is re-thrown. If the second attempt
 * returns a 5xx response, that response is returned so the caller can surface
 * its error payload to the operator.
 *
 * **Only wrap sub-requests that are safe to repeat.** A retry replays the
 * callback:
 * - The presign call is a server-side read (auth + tenant + MIME check; no
 *   state mutation) — safe.
 * - The PUT is create-only since BL-2550: a repeat after a first attempt that
 *   landed answers 412, which {@link putViaSignedUrl} reads as landed.
 * - `/api/files/upload-complete` is wrapped as of BUG-421. The route is now
 *   idempotent on `(tenant_id, entity_id, storage_path)`: the metadata-table
 *   branch uses a partial UNIQUE index + re-SELECT path, and the JSONB-append
 *   branch distinguishes "retry of this caller" from "different upload with
 *   same filename" by comparing `storage_path` before deciding to 409 +
 *   rollback. Two completes of the same body return the same attachment id.
 *
 * @param fn Callback that performs one attempt of the sub-request. May be
 *   invoked up to `MAX_ATTEMPTS` times — must be safe to repeat.
 * @returns The response of whichever attempt produced one (the second, if the
 *   first was transient).
 */
async function retryOn5xx(fn: () => Promise<Response>): Promise<Response> {
  // Initialized with a sentinel so the throw at the end of the function is
  // safe even if a future change to MAX_ATTEMPTS breaks the invariant that
  // the catch branch runs at least once before reaching the throw.
  let lastError: unknown = new Error(
    'retryOn5xx: exhausted attempts without a caught error (unreachable)',
  );
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const res = await fn();
      const isTransient = res.status >= 500 && res.status < 600;
      if (isTransient && attempt < MAX_ATTEMPTS) {
        // Surface the first-attempt status to the console so an operator can
        // still trace "the upload retried because of a 502" during triage —
        // the original Response is discarded by the loop and would otherwise
        // leave no signal.
        console.warn('[retryOn5xx] transient', res.status, '— retrying');
        if (RETRY_DELAY_MS > 0) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        }
        continue;
      }
      return res;
    } catch (error) {
      lastError = error;
      // AbortError is an intentional cancellation (via AbortController) — not a
      // transient failure. Break out so the caller sees the cancellation it
      // requested rather than getting a silent replay. Today no caller plumbs
      // a signal into uploadViaSignedUrl, but cancel-upload UX is a likely
      // next step and this guard keeps the retry helper safe to reuse there.
      // Use the `.name` property rather than `instanceof DOMException` — the
      // class identity isn't stable across test/runtime boundaries (Vitest's
      // DOMException is a separate global), and `.name === 'AbortError'` is
      // the canonical signal across browser + Node fetch implementations.
      const isAbort = (error as { name?: string } | null | undefined)?.name === 'AbortError';
      if (isAbort) break;
      if (attempt < MAX_ATTEMPTS) {
        console.warn('[retryOn5xx] threw — retrying:', error);
        if (RETRY_DELAY_MS > 0) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        }
        continue;
      }
    }
  }
  // Every attempt threw — re-throw the last error so the caller's catch sees it.
  throw lastError;
}

/** A file the browser PUT to the path the presign step minted, ready for its complete step. */
export interface PutFile {
  storagePath: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

/**
 * Result of {@link putViaSignedUrl}: the stored file's facts, or a user-safe error with the HTTP
 * status that refused it (absent when there was none) and the step that refused it, so a caller
 * can retry a server error, and tell our server's 401 from storage refusing an expired URL.
 */
export type PutViaSignedUrlResult =
  | ({ success: true } & PutFile)
  | { success: false; error: string; status?: number; step: 'presign' | 'put' };

/**
 * Steps 1 and 2 of the flow: presign, then PUT the body straight to storage.
 * The caller runs its own complete step — the generic `/api/files/upload-complete`
 * ({@link uploadViaSignedUrl}), or a record's own register route (an invoice
 * attachment, BL-2217).
 *
 * Each sub-request retries once on a transient failure. The PUT is create-only
 * (BL-2550), so a retry that finds its object already written counts as landed.
 *
 * @param params - The parent entity, its tenant and the file.
 * @param onPhase - Optional per-phase progress hook.
 * @returns The stored file's path, name, size and type, or a user-safe error.
 * @sideEffect Writes the object to storage.
 */
export async function putViaSignedUrl(
  params: Omit<UploadViaSignedUrlParams, 'extraMetadata'>,
  onPhase?: (phase: UploadPhase) => void,
): Promise<PutViaSignedUrlResult> {
  const fileName = params.fileName ?? (params.file instanceof File ? params.file.name : 'upload');
  const mimeType = params.mimeType ?? (params.file.type || 'application/octet-stream');
  const fileSize = params.file.size;

  onPhase?.('presign');
  const presignRes = await retryOn5xx(() =>
    // retryOn5xx reads res.status/res.ok directly, so use the typed client's
    // .raw() escape hatch, which returns the raw Response.
    api.files.upload_url.post.raw({
      body: {
        entityType: params.entityType,
        entityId: params.entityId,
        tenantId: params.tenantId,
        fileName,
        fileSize,
        mimeType,
        ...(params.thumbnailOf ? { thumbnailOf: params.thumbnailOf } : {}),
        ...(params.posterOf ? { posterOf: params.posterOf } : {}),
        ...(params.newRecord ? { newRecord: true as const } : {}),
      },
    }),
  );
  if (!presignRes.ok) {
    const err = await presignRes.json().catch(() => ({}));
    return { success: false, error: err.error || `Failed to start upload (${presignRes.status})`, status: presignRes.status, step: 'presign' };
  }
  const presign = await parseResponse<ApiEnvelope<UploadUrlDto>>(presignRes);
  if (!presign.success) {
    return { success: false, error: presign.error || 'Failed to start upload', step: 'presign' };
  }
  const { signedUrl, uploadHeaders, storagePath } = presign.data;

  onPhase?.('put');
  // BL-2550: the signed URL is create-only, and `uploadHeaders` carry the
  // precondition it was signed with, so they go verbatim. A retry follows only
  // a first attempt that threw or answered 5xx, whose outcome is unknown. If
  // that attempt landed, the retry finds the object and answers 412: the
  // upload has landed, because a single PUT is all or nothing and this URL's
  // path (a fresh per-upload id) was minted for this upload alone. A 412 on the
  // first attempt means the object existed before this upload touched it, so
  // it stays a refusal. An expired URL answers 4xx and is not retried.
  let putAttempts = 0;
  const putRes = await retryOn5xx(() => {
    putAttempts += 1;
    return fetch(signedUrl, { method: 'PUT', body: params.file, headers: { 'Content-Type': mimeType, ...uploadHeaders } });
  });
  const landedOnRetry = putRes.status === PRECONDITION_FAILED && putAttempts > 1;
  if (!putRes.ok && !landedOnRetry) {
    const text = await putRes.text().catch(() => '');
    console.error('[putViaSignedUrl] PUT failed:', putRes.status, text);
    return { success: false, error: `Storage upload failed (${putRes.status})`, status: putRes.status, step: 'put' };
  }
  return { success: true, storagePath, fileName, fileSize, mimeType };
}

/**
 * The whole flow: {@link putViaSignedUrl}, then `/api/files/upload-complete`,
 * which re-validates and writes the metadata.
 *
 * - `/api/files/upload-complete` is wrapped as of BUG-421. The route is
 *   idempotent on `(tenant_id, entity_id, storage_path)`: the metadata-table
 *   branch uses a partial UNIQUE index + re-SELECT path, and the JSONB-append
 *   branch distinguishes "retry of this caller" from "different upload with
 *   same filename" by comparing `storage_path` before deciding to 409 +
 *   rollback. Two completes of the same body return the same attachment id.
 *
 * @returns Discriminated-union result. Errors are user-safe strings ready to
 *   surface in a banner — server-side error IDs are logged to the console for
 *   support.
 * @sideEffect Issues 3 network round trips and writes a metadata row / JSONB
 *   entry on success.
 */
export async function uploadViaSignedUrl(
  params: UploadViaSignedUrlParams,
  onPhase?: (phase: UploadPhase) => void,
): Promise<UploadViaSignedUrlResult> {
  try {
    const put = await putViaSignedUrl(params, onPhase);
    if (!put.success) return { success: false, error: put.error };
    const { storagePath, fileName, fileSize, mimeType } = put;

    onPhase?.('complete');
    // Safe to retry as of BUG-421: the route is idempotent on
    // (tenant_id, entity_id, storage_path).
    const completeRes = await retryOn5xx(() =>
      // retryOn5xx reads res.status/res.ok directly, so use the typed client's
      // .raw() escape hatch, which returns the raw Response.
      api.files.upload_complete.post.raw({
        body: {
          entityType: params.entityType,
          entityId: params.entityId,
          tenantId: params.tenantId,
          storagePath,
          fileName,
          fileSize,
          mimeType,
          ...(params.extraMetadata ? { extraMetadata: params.extraMetadata } : {}),
          ...(params.newRecord ? { newRecord: true as const } : {}),
        },
      }),
    );
    if (!completeRes.ok) {
      const err = await completeRes.json().catch(() => ({}));
      return {
        success: false,
        error: err.error || `Upload failed (${completeRes.status})`,
        ...(err.fieldErrors ? { fieldErrors: err.fieldErrors } : {}),
      };
    }
    const complete = await parseResponse<ApiEnvelope<UploadCompleteDto>>(completeRes);
    const warning = complete.success ? complete.warning : undefined;
    const att = complete.success ? complete.data.attachments[0] : undefined;
    if (!att) {
      return { success: false, error: warning || 'No attachment returned' };
    }
    return { success: true, attachment: att, ...(warning ? { warning } : {}) };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('[uploadViaSignedUrl] exception:', error);
    return { success: false, error: message };
  }
}
